import { p, h, ul, code, tip, info, warn, table, cards, steps } from './helpers';

const course = {
  id: 'meet-kamailio',
  step: 3,
  title: 'Meet Kamailio',
  tagline: 'What Kamailio is, how it is built, how to install it and how to run your first config.',
  icon: 'Server',
  colors: ['#b14dff', '#ff4dd8'],
  level: 'Beginner',
  lessons: [
    {
      id: 'what-is-kamailio',
      title: 'What is Kamailio?',
      icon: 'Zap',
      minutes: 8,
      summary: 'An open source SIP server written in C that can handle thousands of call setups per second.',
      blocks: [
        p('**Kamailio** is an open source SIP signaling server, licensed under the GPLv2+, written in C and running on Linux and other Unix-like systems. Telecom operators, cloud communication platforms and enterprises use it as a registrar, proxy, load balancer and security edge in front of their voice and messaging services.'),
        h('A short history'),
        steps([
          { title: '2001: SER', text: 'The SIP Express Router project starts at Fraunhofer FOKUS in Berlin.' },
          { title: '2005: OpenSER', text: 'A community fork with a faster release cycle.' },
          { title: '2008: Kamailio', text: 'OpenSER is renamed Kamailio, a Hawaiian word often translated as "to communicate".' },
          { title: '2010: Reunited', text: 'Kamailio 3.0 merges the SER and Kamailio code bases again.' },
          { title: 'Today', text: 'Major releases roughly once a year (5.x, then 6.x), with a large module catalog and an active community.' },
        ]),
        h('What people build with it'),
        cards([
          { icon: 'BookUser', title: 'Registrar', text: 'Millions of subscriber registrations with database-backed location storage.' },
          { icon: 'Scale', title: 'Load balancer', text: 'Distribute calls across Asterisk, FreeSWITCH or carrier gateways with health checks.' },
          { icon: 'Shield', title: 'Security edge', text: 'Flood detection, IP bans, authentication, TLS and topology hiding in front of PBXs.' },
          { icon: 'Globe', title: 'WebRTC gateway', text: 'SIP over WebSocket for browsers, bridged to classic SIP with RTPEngine.' },
          { icon: 'Users', title: 'Presence and IM', text: 'SUBSCRIBE, NOTIFY, PUBLISH and MESSAGE handling with the presence modules.' },
          { icon: 'Antenna', title: 'IMS / VoLTE', text: 'P-CSCF, I-CSCF and S-CSCF roles with the ims_* modules.' },
        ]),
        h('What Kamailio is not'),
        ul([
          'Not a PBX. There is no voicemail, IVR, queue or conference. Pair it with Asterisk or FreeSWITCH.',
          'Not a media server. It does not decode, transcode, mix or record audio.',
          'Not a B2BUA by design. It is a proxy and keeps the dialog end to end. (Modules like `topos` can hide topology, but it stays a proxy at heart.)',
        ]),
        tip('Performance', 'Because Kamailio only parses signaling and keeps work in shared memory, a single commodity server can typically process thousands of call setups per second and hold millions of registrations, depending on the config and database.'),
      ],
    },
    {
      id: 'architecture',
      title: 'Architecture: core, modules and processes',
      icon: 'Boxes',
      minutes: 11,
      summary: 'A small core, hundreds of loadable modules and a pre-forked multi-process design.',
      blocks: [
        h('Core and modules'),
        p('The **core** does the essentials: parses SIP, manages memory, handles network sockets and runs the routing script. Almost everything else comes from **modules**, shared objects (`.so` files) you load in the config. There are well over 200 of them.'),
        table(['Module', 'What it provides'], [
          ['tm', 'Transaction management, `t_relay()`, failure routes, timers'],
          ['sl', 'Stateless replies, `sl_send_reply()`'],
          ['rr', 'Record-Route and loose routing'],
          ['registrar + usrloc', 'REGISTER processing and the location table'],
          ['auth + auth_db', 'Digest authentication against a database'],
          ['maxfwd, sanity', 'Max-Forwards checking and message sanity checks'],
          ['dispatcher', 'Load balancing and failover'],
          ['nathelper, rtpengine', 'NAT detection, contact fixing and media relay control'],
          ['dialog', 'Dialog state, active call tracking'],
          ['htable', 'Shared-memory hash tables'],
          ['pike', 'Flood detection per source IP'],
          ['jsonrpcs, ctl', 'Management interfaces used by kamcmd and kamctl'],
        ]),
        h('Processes'),
        p('Kamailio is a pre-forked multi-process server. When it starts, the main process parses the config and forks workers:'),
        ul([
          '**UDP receivers**: `children` processes for each UDP socket, each reading and handling messages independently.',
          '**TCP main and workers**: one TCP manager plus `tcp_children` workers that process TCP, TLS and WebSocket traffic.',
          '**Timer processes**: tm retransmissions, usrloc expiry, nathelper pings and other periodic jobs.',
          '**Control processes**: listen for management commands from kamcmd and RPC clients.',
        ]),
        code('bash', `
$ kamcmd core.psx
{ IDX: 0  PID: 2101  DSC: main process - attendant }
{ IDX: 1  PID: 2102  DSC: udp receiver child=0 sock=203.0.113.10:5060 }
{ IDX: 2  PID: 2103  DSC: udp receiver child=1 sock=203.0.113.10:5060 }
{ IDX: 9  PID: 2110  DSC: slow timer }
{ IDX: 10 PID: 2111  DSC: timer }
{ IDX: 11 PID: 2112  DSC: ctl handler }
{ IDX: 12 PID: 2113  DSC: tcp main process }`, 'Listing processes (output trimmed)'),
        h('Two kinds of memory'),
        table(['', 'Private (pkg)', 'Shared (shm)'], [
          ['Belongs to', 'One process', 'All processes'],
          ['Holds', 'The message being parsed, script variables `$var()`', 'Transactions, dialogs, usrloc, htable, `$shv()`'],
          ['Size option', '`-M` in MB (default 8)', '`-m` in MB (default 64)'],
          ['If exhausted', 'That process fails to handle messages', 'Errors everywhere. Raise `-m` on busy servers.'],
        ]),
        info('The script is compiled once', 'At startup Kamailio parses `kamailio.cfg` into an internal action tree. Each SIP message then runs through that tree in whichever process received it. Editing the file has no effect until you restart (some modules can reload their data at runtime, but the routing logic itself needs a restart).'),
      ],
    },
    {
      id: 'installation',
      title: 'Installing Kamailio',
      icon: 'Download',
      minutes: 10,
      summary: 'Install from distribution packages, the official repository or source.',
      blocks: [
        h('Option 1: Debian or Ubuntu packages'),
        p('The quickest start. Distribution packages may lag behind the latest release, but they are well tested.'),
        code('bash', `
sudo apt update
sudo apt install kamailio kamailio-mysql-modules \\
     kamailio-tls-modules kamailio-websocket-modules \\
     kamailio-extra-modules
kamailio -v`),
        h('Option 2: The official Kamailio repository'),
        p('The project publishes packages for each major release at **deb.kamailio.org**, and RPMs for RHEL-family systems. Each release series has its own repository path. Check the install page on kamailio.org for the current series and your distribution name, then add the repository key and source list as described there.'),
        code('bash', `
# Example for the 6.0 series on Debian 12 (bookworm).
# Replace kamailio60 / bookworm with your release and distribution.
echo "deb http://deb.kamailio.org/kamailio60 bookworm main" | \\
  sudo tee /etc/apt/sources.list.d/kamailio.list
# Import the repository signing key as documented on kamailio.org, then:
sudo apt update && sudo apt install kamailio kamailio-mysql-modules`),
        h('Option 3: Build from source'),
        code('bash', `
sudo apt install git gcc g++ flex bison make cmake pkg-config \\
     libssl-dev default-libmysqlclient-dev libcurl4-openssl-dev
git clone --depth 1 --branch 6.0 https://github.com/kamailio/kamailio kamailio
cd kamailio

# Kamailio 6.x builds with CMake:
cmake -B build -DINCLUDE_MODULES="db_mysql tls websocket"
cmake --build build -j"$(nproc)"
sudo cmake --install build

# Kamailio 5.x used the classic make flow:
#   make include_modules="db_mysql tls websocket" cfg
#   make all && sudo make install`),
        h('Where things live'),
        table(['Path', 'Contents'], [
          ['`/etc/kamailio/kamailio.cfg`', 'The routing script and configuration'],
          ['`/etc/kamailio/kamctlrc`', 'Settings for kamctl and kamdbctl (database engine, SIP domain)'],
          ['`/etc/kamailio/tls.cfg`', 'TLS certificates and profiles'],
          ['`/usr/lib/x86_64-linux-gnu/kamailio/modules/`', 'Module `.so` files on Debian (source installs use `/usr/local/lib64/kamailio/modules/`)'],
          ['`/etc/default/kamailio`', 'Debian defaults, including shared and private memory sizes'],
        ]),
        h('Running as a service'),
        code('bash', `
kamailio -c -f /etc/kamailio/kamailio.cfg   # syntax check, exits immediately
sudo systemctl enable --now kamailio
sudo systemctl status kamailio
journalctl -u kamailio -f                   # follow the logs`),
        warn('Always check before restarting', 'A typo in kamailio.cfg prevents Kamailio from starting at all, dropping every call on the server. Run `kamailio -c` first, every time.'),
      ],
    },
    {
      id: 'first-run',
      title: 'Your first config',
      icon: 'Rocket',
      minutes: 12,
      summary: 'A tiny stateless config that answers pings, then a stateful proxy in about 40 lines.',
      blocks: [
        p('The default `kamailio.cfg` is long because it covers many scenarios. Start from something you can read in one screen.'),
        code('cfg', `
#!KAMAILIO

debug=2
log_stderror=yes
listen=udp:127.0.0.1:5060
children=2

loadmodule "sl.so"
loadmodule "textops.so"
loadmodule "xlog.so"

request_route {
    xlog("L_INFO", "got $rm from $si:$sp for $ru\\n");

    if (is_method("OPTIONS")) {
        sl_send_reply("200", "Hello from Kamailio");
        exit;
    }
    sl_send_reply("404", "Nothing here yet");
}`, 'hello.cfg'),
        p('Run it in the foreground with logging to the terminal, then ping it from a second terminal with **sipsak** or **sipexer**:'),
        code('bash', `
kamailio -DD -E -f hello.cfg          # -DD: stay in foreground, -E: log to stderr
sipsak -vv -s sip:ping@127.0.0.1      # sends OPTIONS, prints the 200 reply`),
        h('Adding state and routing'),
        code('cfg', `
#!KAMAILIO

debug=2
log_stderror=no
log_facility=LOG_LOCAL0
listen=udp:0.0.0.0:5060
alias="example.com"
children=4

loadmodule "tm.so"
loadmodule "sl.so"
loadmodule "rr.so"
loadmodule "maxfwd.so"
loadmodule "textops.so"
loadmodule "siputils.so"
loadmodule "xlog.so"

modparam("tm", "fr_inv_timer", 30000)   # 30 s ringing timeout

request_route {
    if (!mf_process_maxfwd_header("10")) {
        sl_send_reply("483", "Too Many Hops");
        exit;
    }

    # requests inside a dialog follow the Route set
    if (has_totag()) {
        if (loose_route()) {
            t_relay();
            exit;
        }
        if (is_method("ACK") && t_check_trans()) {
            t_relay();
        }
        exit;
    }

    if (is_method("INVITE|SUBSCRIBE")) {
        record_route();
    }

    # send every new call to our PBX
    $du = "sip:10.0.0.20:5060";
    if (!t_relay()) {
        sl_reply_error();
    }
}`, 'proxy.cfg: a stateful forwarding proxy'),
        ul([
          '`#!KAMAILIO` on the first line tells Kamailio this is native Kamailio syntax.',
          '`alias` lists other names Kamailio answers to, so `uri==myself` matches them.',
          '`$du` sets the **destination URI**: where the packet is sent, without changing the Request-URI.',
          '`t_relay()` creates a transaction, sends `100 Trying` for INVITEs, retransmits over UDP and forwards replies back.',
        ]),
        tip('Watch it live', 'Run `sudo sngrep` on the server while you place a call. It draws a live ladder diagram from the captured packets and is the fastest way to see what your script really does.'),
      ],
    },
    {
      id: 'tools',
      title: 'kamctl, kamcmd and friends',
      icon: 'TerminalSquare',
      minutes: 9,
      summary: 'Command-line tools to manage subscribers, inspect state and change settings at runtime.',
      blocks: [
        table(['Tool', 'What it does'], [
          ['`kamctl`', 'Shell script for daily admin: subscribers, ACL, location, dispatcher, address tables. Reads `/etc/kamailio/kamctlrc`.'],
          ['`kamdbctl`', 'Creates, migrates and drops the Kamailio database schema.'],
          ['`kamcmd`', 'Fast binary RPC client talking to the `ctl` module. Inspect and change state in a running server.'],
          ['`kamcli`', 'A newer Python-based CLI covering most kamctl and kamcmd tasks, with JSON-RPC support.'],
          ['`sngrep`', 'Terminal UI that captures SIP and draws call flows. Not part of Kamailio, but essential.'],
        ]),
        h('Everyday commands'),
        code('bash', `
# subscribers (needs a database, see Step 6)
kamctl add alice@example.com S3cret!      # create
kamctl passwd alice@example.com N3wPass  # change password
kamctl rm alice@example.com               # delete

# who is registered?
kamctl ul show
kamcmd ul.dump

# server health
kamcmd core.version
kamcmd core.uptime
kamcmd stats.get_statistics all
kamcmd tm.stats

# turn up log verbosity without a restart
kamcmd cfg.seti core debug 3

# list all available RPC commands
kamcmd system.listMethods`),
        h('RPC over HTTP'),
        p('With the `jsonrpcs` and `xhttp` modules, a TCP listener and an `event_route[xhttp:request]` that calls `jsonrpc_dispatch()`, you can call the same RPC commands over HTTP. The default config enables this with `#!define WITH_JSONRPC`. It is handy for dashboards and automation:'),
        code('bash', `
curl -s -X POST http://127.0.0.1:5060/RPC \\
  -H 'Content-Type: application/json' \\
  -d '{"jsonrpc":"2.0","method":"core.uptime","id":1}'`),
        info('Protect your management interfaces', 'RPC commands can change routing, drop registrations or reload tables. Keep the ctl socket local and restrict the HTTP RPC path to trusted IPs in your script.'),
      ],
    },
  ],
  quiz: [
    { q: 'Which statement about Kamailio is true?', options: ['It transcodes audio between codecs', 'It is a SIP signaling server that does not process media itself', 'It is a PBX with voicemail and IVR', 'It only supports SIP over UDP'], answer: 1, explain: 'Kamailio handles SIP signaling. Media is handled by endpoints or a relay such as RTPEngine.' },
    { q: 'Which memory pool holds transactions and the location table?', options: ['Private (pkg) memory', 'Shared (shm) memory', 'The stack', 'The database only'], answer: 1, explain: 'Data that all processes need lives in shared memory, sized with -m.' },
    { q: 'What does kamailio -c do?', options: ['Clears the cache', 'Checks the config file syntax and exits', 'Connects to the database', 'Counts active calls'], answer: 1, explain: 'Always run kamailio -c after editing the config and before restarting.' },
    { q: 'Which variable changes where a request is sent without changing the Request-URI?', options: ['$ru', '$du', '$fu', '$tu'], answer: 1, explain: '$du is the destination URI, the next hop. $ru is the Request-URI.' },
    { q: 'Which tool would you use to see live call flows on the server?', options: ['kamdbctl', 'sngrep', 'kamctl add', 'systemctl'], answer: 1, explain: 'sngrep captures SIP packets and draws ladder diagrams in the terminal.' },
  ],
};

export default course;
